import { useEffect, useRef, type KeyboardEvent } from "react";

import { formatMetres } from "@/lib/format";

import { Button } from "./Button";
import styles from "./FigureLossAsk.module.css";

/** A figure the gesture would take: the wall as a person counts it, and what it says. */
export interface FigureAtRisk {
  wallNumber: number;
  lengthMm: number;
  /** „gelesen“ where the reading had it, „eingetragen“ where a person typed it. */
  read: boolean;
}

interface FigureLossAskProps {
  /** The id of the title — the group's name; the sentence below it describes the group. */
  questionId: string;
  figures: FigureAtRisk[];
  /** Abbrechen: the figures stay (Escape says the same). */
  onCancel: () => void;
  /** Trotzdem verschieben: the gesture counts, and the walls lose their figures. */
  onGoAhead: () => void;
  /** Where the screen makes room — the board's „Maximieren“ floats over the top right of its frame. */
  className?: string;
}

/** `Wand 1 (2,42 m gelesen)`, `Wand 1 …, Wand 2 … und Wand 3 …` — and the verb that agrees. */
function sentenceOf(figures: FigureAtRisk[]): string {
  const names = figures.map(
    ({ wallNumber, lengthMm, read }) => `Wand ${wallNumber} (${formatMetres(lengthMm)} ${read ? "gelesen" : "eingetragen"})`,
  );
  if (names.length === 1) return `${names[0]} verliert ihr Maß.`;
  return `${names.slice(0, -1).join(", ")} und ${names.at(-1)} verlieren ihr Maß.`;
}

/**
 * „Maß geht verloren“ — asked before a push or a pulled corner takes the
 * figure off a wall that had one (spec 2026-10-28 §3.4, B-4: a wall pushed one
 * step, and two read figures went without a word). Inline above the board, in
 * the form of `LengthDoubt` and not a dialog: the drawing stays in view, and
 * what the gesture did is what the person is looking at.
 *
 * A group named by its title, the sentence as its description. The focus goes
 * to **Abbrechen** — the answer that changes nothing is the one Enter gives.
 * Escape is the same answer.
 */
export function FigureLossAsk({ questionId, figures, onCancel, onGoAhead, className }: FigureLossAskProps) {
  const cancel = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancel.current?.focus();
  }, []);

  function keyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== "Escape") return;
    event.preventDefault();
    event.stopPropagation();
    onCancel();
  }

  return (
    <div
      role="group"
      aria-labelledby={`${questionId}-title`}
      aria-describedby={questionId}
      className={`${styles.ask} ${className ?? ""}`}
      data-role="figure-loss-ask"
      onKeyDown={keyDown}
    >
      <p id={`${questionId}-title`} className={styles.title}>
        Maß geht verloren
      </p>
      <p id={questionId} className={styles.question}>
        {sentenceOf(figures)}
      </p>
      <div className={styles.answers}>
        <Button ref={cancel} onClick={onCancel}>
          Abbrechen
        </Button>
        <Button onClick={onGoAhead}>Trotzdem verschieben</Button>
      </div>
    </div>
  );
}
